import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { useCallback, useEffect } from 'react';
import { type MapGeometryItem } from '../createMapSchema.ts';

/**
 * Alloca gli indici per la numerazione delle geometrie ("# 1", "# 2", …)
 * derivandoli dalle geometrie presenti nel form: il prossimo indice è sempre
 * `max(geometryIndex esistenti) + 1`. Nessuno stato persistito: dopo la
 * rimozione della geometria con indice più alto il numero può essere riusato,
 * e dopo un reset la numerazione riparte da 1. Al mount, assegna
 * automaticamente un geometryIndex alle geometrie pre-esistenti che ne sono
 * prive.
 */
export function useMapGeometryCounter(
  editor: RelationEditor<MapGeometryItem>,
  readonly?: boolean,
) {
  /** Indice più alto tra le geometrie attualmente presenti nel form (0 se vuoto). */
  const getHighestExistingIndex = useCallback(() => {
    const indexes = (editor.getValue() ?? []).map(i => i.geometryIndex ?? 0);
    return indexes.length > 0 ? Math.max(...indexes) : 0;
  }, [editor]);

  /**
   * Restituisce il prossimo indice disponibile. Usato per l'evento `add`
   * (disegno singolo o aggiunta da ricerca catastale).
   */
  const getNextIndex = useCallback(
    () => getHighestExistingIndex() + 1,
    [getHighestExistingIndex],
  );

  /**
   * Restituisce un array di `count` indici sequenziali a partire dal
   * successivo disponibile. Usato per l'evento `upload` (caricamento file
   * con più geometrie).
   *
   * @param count - Numero di indici da generare
   * @returns Array di indici sequenziali, es. `[4, 5, 6]`
   */
  const getNextIndexBatch = useCallback(
    (count: number) => {
      const start = getHighestExistingIndex() + 1;
      return Array.from({ length: count }, (_, i) => start + i);
    },
    [getHighestExistingIndex],
  );

  // Retrocompatibilità: assegna geometryIndex alle geometrie pre-numerazione.
  // Al mount, se ci sono geometrie senza geometryIndex (dati salvati prima
  // dell'introduzione della numerazione), gli assegna indici progressivi
  // e aggiorna il form. Se tutte le geometrie hanno già un geometryIndex,
  // non fa nulla.
  useEffect(() => {
    if (readonly) return;
    const items = editor.getValue() ?? [];
    // Trova le geometrie senza indice
    const unindexed = items.filter(i => i.geometryIndex == null);
    if (unindexed.length === 0) return;
    // Genera un batch di indici freschi a partire dal max esistente
    const idxs = getNextIndexBatch(unindexed.length);
    // Riscrive gli items nel form assegnando i nuovi geometryIndex
    let counter = 0;
    editor.replace(
      items.map(item =>
        item.geometryIndex != null
          ? item
          : { ...item, geometryIndex: idxs[counter++] },
      ),
    );
  }, [editor, getNextIndexBatch, readonly]);

  return { getNextIndex, getNextIndexBatch };
}
